import React from "react";
import AlloyEditor from "alloyeditor";
import { Button, Icon } from "armstrong-react";
import { Grid, Row, Col } from 'armstrong-react-fix';
import { IUploader, createXmlHttpUploader } from "./../../../core/uploader/uploader";
import { BlobReader } from "./../../../core/uploader/blobReader";
import { ThumbnailProvider } from "./../../../core/uploader/thumbnailProvider";
import { ChunkSizeProvider } from "./../../../core/uploader/chunkSizeProvider";
import { TaskQueueStatus } from "./../../../core/uploader/tasks/taskProcessor";

import "./alloyEditorSkin.scss";
import "./alloyEditorComponent.scss";

export enum EditorLevel {
  minimal,
  minimalWithImage,
  simple,
  advanced
}

enum EditorMode {
  wsywig,
  raw,
  preview
}

interface IAlloyEditorComponentProps extends React.Props<AlloyEditorComponent> {
  className?: string;
  onChange?: (content: string) => void;
  value?: string;
  editorLevel?: EditorLevel;
  placeholder?: string;
  imageUploadType: string;
  disabled?: boolean;
  imageUploadMetadata?: { [ key: string]: string|number };
}

export class AlloyEditorComponent extends React.Component<IAlloyEditorComponentProps, { editorMode?: EditorMode }> {
  private uploader: IUploader;
  private imageElement;
  private editable: HTMLDivElement;
  private editor;
  private multimediaPlugins = "ae_uicore,ae_selectionregion,ae_selectionkeystrokes,ae_dragresize,ae_imagealignment,ae_addimages,ae_placeholder,ae_tabletools,ae_tableresize,ae_autolink,ae_embed,ae_autolist,ae_buttonbridge,widget,dialog,image,oembed,htmlwriter";
  private imagePlugins = "ae_uicore,ae_selectionregion,ae_selectionkeystrokes,ae_dragresize,ae_addimages,ae_placeholder,ae_tabletools,ae_tableresize,ae_autolink,ae_embed,ae_autolist,ae_buttonbridge,widget,image,htmlwriter";
  private standardPlugins = "ae_uicore,ae_selectionregion,ae_selectionkeystrokes,ae_dragresize,ae_imagealignment,ae_placeholder,ae_tabletools,ae_tableresize,ae_autolink,ae_autolist,htmlwriter";
  private multimediaButtons = ["image", "embed", "hline", "table", "oembed"];
  private minimalWithImageButtons = ["image"];
  private standardButtons = ["hline"];

  public constructor(props: IAlloyEditorComponentProps) {
    super(props);

    const fileExtensionRegex = /(.+)\.([^.]+)?$/;

    this.state = { editorMode: EditorMode.wsywig }
    this.uploader = this.createUploader(`${globalConfig.uploadBaseUrl}/upload`);
    this.uploader.subscribe(() => {
      let uploads = this.uploader.getUploads();
      if (uploads.length !== 0) {
        let upload = uploads[0];
        var status = upload.getStatus();
        if (status === TaskQueueStatus.Complete) {
          const filename = upload["taskProvider"].uploadedFilename;
          const matches = fileExtensionRegex.exec(filename);
          const filenameWithoutExtension = matches[1];
          const extension = matches[2];
          let url = `${globalConfig.cdnBaseUrl}/${this.props.imageUploadType}/${filenameWithoutExtension}_small.${extension}`;

          this.imageElement.src = url;
          this.imageElement.classList.toggle('uploading');
          this.imageElement = null;
          upload.remove();
        }
      }
    })
  }

  private initEditor() {
    let selections = [
      {
        name: 'link',
        buttons: ['linkEdit'],
        test: AlloyEditor.SelectionTest.link
      },
      {
        name: 'text',
        buttons: ['styles', 'bold', 'italic', 'underline', 'link'],
        test: AlloyEditor.SelectionTest.text
      },
      {
        name: 'table',
        buttons: ['tableRow', 'tableColumn', 'tableCell', 'tableRemove'],
        getArrowBoxClasses: AlloyEditor.SelectionGetArrowBoxClasses.table,
        setPosition: AlloyEditor.SelectionSetPosition.table,
        test: AlloyEditor.SelectionTest.table
      }
    ];

    if (this.props.editorLevel === EditorLevel.advanced) {
      selections.push({
        name: 'image',
        buttons: ['imageLeft', 'imageRight'],
        test: AlloyEditor.SelectionTest.image
      });
    }

    let config = {
      buttonCfg: {
        linkEdit: {
          defaultLinkTarget: '_blank'
        }
      },
      toolbars: {
        styles: {
          selections
        },
        add: {
          buttons: this.getEditorButtons()
        }
      },
      extraPlugins: this.getEditorPlugins(),
      readOnly: this.props.disabled
    }

    if (this.props.editorLevel === EditorLevel.minimal) {
      delete config.toolbars.add;
    }
    
    this.editable.innerHTML = this.props.value;
    this.editor = AlloyEditor.editable(this.editable, config)

    let nativeEditor = this.editor.get('nativeEditor');

    nativeEditor.on('imageAdd', (event) => {
      this.uploader.enqueueFile(event.data.file, "image", this.props.imageUploadType, this.props.imageUploadMetadata);
      this.imageElement = event.data.el.$;
      this.imageElement.width = 400;
      this.imageElement.classList.toggle('uploading');
    });

    nativeEditor.on('instanceReady', function (event) {
      this.dataProcessor.writer.setRules('p', {
        indent: false,
        breakBeforeOpen: true,
        breakAfterOpen: false,
        breakBeforeClose: false,
        breakAfterClose: true
      });
    });

    nativeEditor.on('change', (event) => {
      if (this.props.onChange) {
        this.props.onChange(this.getHtml())
      }
    });

    nativeEditor.on('blur', (event) => {
      if (this.props.onChange) {
        this.props.onChange(this.getHtml())
      }
    });
  }

  private getEditorButtons() {
    switch (this.props.editorLevel) {
      case EditorLevel.advanced:
        return this.multimediaButtons;
      case EditorLevel.simple:
        return this.standardButtons;
      case EditorLevel.minimalWithImage:
        return this.minimalWithImageButtons;
      case EditorLevel.minimal:
        return [];
    }
  }

  private getEditorPlugins() {
    switch (this.props.editorLevel) {
      case EditorLevel.advanced:
        return this.multimediaPlugins;
      case EditorLevel.minimalWithImage:
        return this.imagePlugins;
      case EditorLevel.simple:
      case EditorLevel.minimal:
        return this.standardPlugins;
    }
  }

  private createUploader(url: string): IUploader {
    return createXmlHttpUploader(url, new BlobReader(), new ThumbnailProvider(), new ChunkSizeProvider());
  }

  private changeEditMode(editorMode: EditorMode) {
    if (this.state.editorMode === editorMode) {
      return;
    }

    if (this.state.editorMode === EditorMode.wsywig && this.editor && this.props.onChange) {
      this.props.onChange(this.getHtml());
    }

    this.setState({ editorMode: editorMode }, () => {
      if (this.state.editorMode === EditorMode.wsywig) {
        this.initEditor();
      }
    })
  }
  
  public clearContent = () => this.editor.get('nativeEditor').setData("");
  
  public getHtml = (): string => this.editor.get('nativeEditor').getData();

  public insertText = (string: string): void => this.editor.get('nativeEditor').insertText(` ${string} `);

  public componentDidMount() {
    this.initEditor()
  }

  public componentWillUnmount() {
    // We don't really care about these errors

    try {
      // destroy secret CKEditor div
      // Alloy doesn't handle dismount during initialise, so this throws
      this.editor.destroy();
    }
    catch(e) { }

    try {
      // remove leftover alloy ui      
      let leftovers = document.getElementsByClassName("ae-sr-only");
      for (let i = 0; i < leftovers.length; i++) {
        let leftover = leftovers[i];
        leftover.parentElement.removeChild(leftover);
      }
    }
    catch(e) { }
  }

  public render() {
    return (
      <div className={this.props.className}>
        {this.props.editorLevel !== EditorLevel.minimal && this.props.editorLevel !== EditorLevel.minimalWithImage &&
          <Grid>
            <Row className="p-bottom-small">
              <Col>
                <h3>Content</h3>
              </Col>
              <Col width="auto" className="editor-mode-buttons">
                <Button className="bg-info" leftIcon={Icon.Icomoon.pen} onClick={() => this.changeEditMode(EditorMode.wsywig)}>Editor</Button>
                <Button className="bg-info" leftIcon={Icon.Icomoon.embed2} onClick={() => this.changeEditMode(EditorMode.raw)}>HTML</Button>
                <Button className="bg-info" leftIcon={Icon.Icomoon.search} onClick={() => this.changeEditMode(EditorMode.preview)}>Preview</Button>
              </Col>
            </Row>
          </Grid>
        }
        {this.state.editorMode === EditorMode.wsywig &&
          <div data-placeholder={this.props.placeholder} className="alloy-editable p-small" ref={r => this.editable = r} />}
        {this.state.editorMode === EditorMode.preview &&
          <Grid>
            <Row>
              <Col><div className="alloy-preview" dangerouslySetInnerHTML={{ __html: this.props.value }} /></Col>
              {this.props.editorLevel === EditorLevel.advanced && <Col className="m-left-medium bg-gray-very-light" width={300}></Col>}
            </Row>
          </Grid>
        }
        {this.state.editorMode === EditorMode.raw &&
          <textarea className="alloy-raw p-small" defaultValue={this.props.value} onChange={e => this.props.onChange((e.target as HTMLTextAreaElement).value)} />}
      </div>
    )
  }
}